// src/components/tours/InitialTours.jsx
import React, { useState, useEffect } from 'react';
import Joyride from 'react-joyride';
import { useAuth } from '../../context/AuthContext.jsx';
import { THEME_CLASSES, PALETTE } from '@/lib/theme.js';
import { X, TramFront } from 'lucide-react';

export default function InitialTours({ isDark }) {
    const { user, markTourComplete, activeTour, endTour } = useAuth();
    const [run, setRun] = useState(false);

    useEffect(() => {
        if (activeTour === 'intro_tour') {
            const timer = setTimeout(() => setRun(true), 500);
            return () => clearTimeout(timer);
        } else if (user && (!user.onboarding_state || !user.onboarding_state['intro_tour'])) {
            const timer = setTimeout(() => setRun(true), 1500);
            return () => clearTimeout(timer);
        }
    }, [user, activeTour]);

    const steps = [
        {
            target: 'body',
            title: 'Welcome to Rapid Jaguar',
            placement: 'center',
            disableBeacon: true,
            content: (
                <div className="space-y-3 mt-2 text-left">
                    <p>Before we jump into the data, here is a quick overview of your command center:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>1. Overview Dashboard:</strong> Monitor Key Performance Indicators (KPIs) and overall activity status at a glance. Track successful imports, total storage, and system health efficiently.</li>
                        <li><strong>Sidebar Navigation:</strong> Access the core engine to manage your data pipeline—from initial ingestion to the final structured Warehouse.</li>
                        <li><strong>User Menu (Top Right):</strong> Click your avatar to access your <em>User Profile</em> (MFA & Security) or <em>Tenant Settings</em> (Team access & Billing).</li>
                    </ul>
                </div>
            )
        },
        {
            target: '.tour-nav-sources',
            title: '2. Data Sources',
            content: 'Manage and configure your data ingestion streams. Use pre-built API connectors, set custom sync strategies, or upload flat files. Monitor connection statuses and record counts all in one place.',
            placement: 'right',
        },
        {
            target: '.tour-nav-raw',
            title: '3. Raw Tables',
            content: 'Data flows here completely unadulterated. Inspect data structure, view sync statuses, and use the built-in query tool for spot-checking and grouping without needing external SQL tools.',
            placement: 'right',
        },
        {
            target: '.tour-nav-dims',
            title: '4. Dimensions',
            content: 'Organize your data by creating dimensions from Raw Tables or Flat Files. Select specific columns via schema auto-loading and seamlessly map relationships using the visual builder.',
            placement: 'right',
        },
        {
            target: '.tour-nav-pipes',
            title: '5. Pipelines',
            content: 'Extract, Transform, and Load (ETL) your Raw Tables into structured Warehouse Tables. Automate updates, handle complex data transformations, and apply record-level filtering seamlessly.',
            placement: 'right',
        },
        {
            target: '.tour-nav-warehouse',
            title: '6. Warehouse Tables',
            content: 'View the refined, aggregated output of your pipelines. This view features the exact same intuitive column listing and query builder tools as Raw Tables for a consistent analytical experience.',
            placement: 'right',
        },
        {
            target: '.tour-nav-profile',
            title: '7. Settings & Credentials',
            content: 'Click your avatar to access your User Settings (like MFA) and Tenant Settings (like Billing). When you are ready to visualize your data, this is also where you will grab your dedicated Postgres credentials to plug directly into Looker, Tableau, or Power BI.',
            placement: 'bottom-end',
        },
        {
            target: 'button[title="Replay Page Tour"]',
            title: '8. Need a Refresh?',
            content: 'If you ever want a refresh of everything, use this play button on the screen you want to learn more about. It will launch a guided walkthrough specific to that section.',
            placement: 'bottom-end',
        }
    ];

    const handleJoyrideCallback = (data) => {
        const { status } = data;
        const finishedStatuses = ['finished', 'skipped'];

        if (finishedStatuses.includes(status)) {
            setRun(false);

            if (activeTour === 'intro_tour') endTour();

            if (!user?.onboarding_state?.['intro_tour']) {
                markTourComplete('intro_tour');
            }
        }
    };

    const CustomTooltip = ({ index, step, tooltipProps, primaryProps, backProps, skipProps, isLastStep, closeProps }) => {
        const { style, ...restTooltipProps } = tooltipProps;

        return (
            <div
                {...restTooltipProps}
                className={`max-w-md rounded-2xl shadow-2xl border p-6 ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}`}
            >
                <div className="flex justify-between items-start mb-3">
                    <h3 className={`text-lg font-bold ${THEME_CLASSES.textPrimary(isDark)}`}>
                        {step.title}
                    </h3>
                    <button {...closeProps} className={`${THEME_CLASSES.textSecondary(isDark)} hover:opacity-70 transition-opacity`}>
                        <X size={18} />
                    </button>
                </div>

                <div className={`text-sm mb-6 leading-relaxed ${THEME_CLASSES.textSecondary(isDark)}`}>
                    {step.content}
                </div>

                <div className="flex items-center justify-between mt-4">
                    <button
                        {...skipProps}
                        className={`flex items-center justify-center p-1.5 rounded-lg transition-colors text-red-500 ${THEME_CLASSES.bgHover(isDark)} hover:bg-red-500/10`}
                        title="Exit Tour"
                    >
                        <TramFront size={18} />
                    </button>

                    <div className="flex items-center gap-2">
                        <span className={`text-[11px] font-medium mr-2 ${THEME_CLASSES.textSecondary(isDark)}`}>
                            Step {index + 1} of {steps.length}
                        </span>

                        {index > 0 && (
                            <button
                                {...backProps}
                                className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}
                            >
                                Back
                            </button>
                        )}
                        <button
                            {...primaryProps}
                            className={`px-4 py-2 text-xs font-bold rounded-lg text-white transition-colors`}
                            style={{ backgroundColor: isDark ? PALETTE.green : PALETTE.greenLight }}
                        >
                            {isLastStep ? 'Complete Tour' : 'Next'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Joyride
            callback={handleJoyrideCallback}
            continuous
            hideCloseButton
            run={run}
            scrollToFirstStep
            showProgress
            showSkipButton
            steps={steps}
            tooltipComponent={CustomTooltip}
            styles={{
                options: {
                    zIndex: 10000,
                    primaryColor: isDark ? PALETTE.green : PALETTE.greenLight,
                },
                overlay: {
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
                }
            }}
        />
    );
}